import { SearchableSelect } from "@/components/molecules/searchable-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { getFrenchPublicHolidays } from "@/lib/french-holidays";
import { getNextOccurrence } from "@/lib/rrule-utils";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { TimeSlots } from "./time-slots";

interface TimeSlot {
  id: string;
  from: string;
  to: string;
}

export interface Exception {
  id: string;
  rrule?: string; // RRULE pattern for recurring exceptions (e.g., yearly holidays)
  date?: string; // YYYY-MM-DD for one-time exceptions
  name: string;
  slots: TimeSlot[];
}

interface ExceptionsEditorProps {
  exceptions: Exception[];
  onChange: (exceptions: Exception[]) => void;
}

export function ExceptionsEditor({
  exceptions,
  onChange,
}: ExceptionsEditorProps) {
  const { t } = useTranslation("schedules");
  const { format } = useDateFormat();
  const [showExceptionForm, setShowExceptionForm] = useState(false);
  const [exceptionType, setExceptionType] = useState<"holiday" | "custom">(
    "holiday",
  );
  const [customDate, setCustomDate] = useState<string>("");
  const [customName, setCustomName] = useState<string>("");

  // The name is what the exception is saved under, so it is written in the
  // language of whoever adds it.
  const holidays = getFrenchPublicHolidays().map((holiday) => ({
    ...holiday,
    name: t(`holidays.${holiday.id}`, holiday.name),
  }));

  // Get available holidays (not already in exceptions)
  const availableHolidays = useMemo(
    () =>
      holidays.filter(
        (h) => !exceptions.some((e) => e.rrule && e.rrule === h.rrule),
      ),
    [holidays, exceptions],
  );

  // Track selected holiday IDs for multi-select
  const [selectedHolidayIds, setSelectedHolidayIds] = useState<string[]>([]);

  // Update selected holidays only when form opens (not when available holidays change)
  useEffect(() => {
    if (showExceptionForm && exceptionType === "holiday") {
      setSelectedHolidayIds(availableHolidays.map((h) => h.id));
    }
  }, [showExceptionForm, exceptionType]); // Removed availableHolidays from dependencies

  const addMinutesToTime = (time: string, minutes: number): string => {
    const [hours, mins] = time.split(":").map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMins = totalMinutes % 60;
    return `${String(newHours).padStart(2, "0")}:${String(newMins).padStart(2, "0")}`;
  };

  const addException = () => {
    if (exceptionType === "holiday" && selectedHolidayIds.length > 0) {
      const newExceptions = selectedHolidayIds
        .map((holidayId) => {
          const holiday = holidays.find((h) => h.id === holidayId);
          if (!holiday) return null;

          return {
            id: `exception-${Date.now()}-${holidayId}`,
            rrule: holiday.rrule,
            name: holiday.name,
            slots: [],
          };
        })
        .filter(Boolean) as Exception[];

      onChange([...exceptions, ...newExceptions]);
      setShowExceptionForm(false);
      setSelectedHolidayIds([]);
    } else if (exceptionType === "custom" && customDate && customName) {
      const newException: Exception = {
        id: `exception-${Date.now()}`,
        date: customDate,
        name: customName,
        slots: [],
      };

      onChange([...exceptions, newException]);
      setShowExceptionForm(false);
      setCustomDate("");
      setCustomName("");
    }
  };

  const removeException = (id: string) => {
    onChange(exceptions.filter((e) => e.id !== id));
  };

  const addExceptionSlot = (exceptionId: string) => {
    const exception = exceptions.find((e) => e.id === exceptionId);
    if (!exception) return;

    let from = "09:00";
    let to = "17:00";

    if (exception.slots.length > 0) {
      const lastSlot = exception.slots[exception.slots.length - 1];
      const [endHour] = lastSlot.to.split(":").map(Number);

      if (endHour < 13) {
        from = "14:00";
        to = "19:00";
      } else {
        from = addMinutesToTime(lastSlot.to, 15);
        to = addMinutesToTime(from, 480);
      }
    }

    onChange(
      exceptions.map((e) =>
        e.id === exceptionId
          ? {
              ...e,
              slots: [
                ...e.slots,
                { id: `slot-${Date.now()}-${Math.random()}`, from, to },
              ],
            }
          : e,
      ),
    );
  };

  const removeExceptionSlot = (exceptionId: string, index: number) => {
    onChange(
      exceptions.map((e) =>
        e.id === exceptionId
          ? { ...e, slots: e.slots.filter((_, i) => i !== index) }
          : e,
      ),
    );
  };

  const updateExceptionSlot = (
    exceptionId: string,
    index: number,
    field: "from" | "to",
    value: string,
  ) => {
    onChange(
      exceptions.map((e) => {
        if (e.id !== exceptionId) return e;

        const newSlots = [...e.slots];
        const currentSlot = newSlots[index];

        if (field === "from") {
          const minTo = addMinutesToTime(value, 15);
          if (currentSlot.to <= value) {
            currentSlot.to = minTo;
          }
          currentSlot.from = value;

          if (newSlots[index + 1]) {
            const minNextFrom = addMinutesToTime(currentSlot.to, 15);
            if (newSlots[index + 1].from < minNextFrom) {
              newSlots[index + 1].from = minNextFrom;
              const minNextTo = addMinutesToTime(minNextFrom, 15);
              if (newSlots[index + 1].to < minNextTo) {
                newSlots[index + 1].to = minNextTo;
              }
            }
          }
        } else {
          const minTo = addMinutesToTime(currentSlot.from, 15);
          if (value < minTo) {
            value = minTo;
          }
          currentSlot.to = value;

          if (newSlots[index + 1]) {
            const minNextFrom = addMinutesToTime(value, 15);
            if (newSlots[index + 1].from < minNextFrom) {
              newSlots[index + 1].from = minNextFrom;
              const minNextTo = addMinutesToTime(minNextFrom, 15);
              if (newSlots[index + 1].to < minNextTo) {
                newSlots[index + 1].to = minNextTo;
              }
            }
          }
        }

        return { ...e, slots: newSlots };
      }),
    );
  };

  return (
    <div className="space-y-2">
      <div className="text-base font-medium">{t("exceptions.title")}</div>

      {exceptions.map((exception) => (
        <div
          key={exception.id}
          className="space-x-2 flex flex-row items-start group/exception"
        >
          <div className="grow">
            <TimeSlots
              label={(() => {
                const nextDate = getNextOccurrence(exception);
                const dateStr = nextDate
                  ? format(nextDate + "T00:00:00", "PP")
                  : t("exceptions.past");
                const recurrence = exception.rrule
                  ? t("exceptions.yearly")
                  : t("exceptions.oneTime");
                return exception.name
                  ? `${exception.name} - ${dateStr} (${recurrence})`
                  : exception.date || t("exceptions.exception");
              })()}
              slots={exception.slots}
              onAddSlot={() => addExceptionSlot(exception.id)}
              onRemoveSlot={(idx) => removeExceptionSlot(exception.id, idx)}
              onUpdateSlot={(idx, field, value) =>
                updateExceptionSlot(exception.id, idx, field, value)
              }
              showAddButton={true}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="xsicon"
            className="hover:text-red-600 hidden group-hover/exception:inline-flex"
            onClick={() => removeException(exception.id)}
          >
            <TrashIcon weight="fill" />
          </Button>
        </div>
      ))}

      {!showExceptionForm && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setShowExceptionForm(true)}
        >
          <PlusIcon className="h-4 w-4" />
          {t("exceptions.add")}
        </Button>
      )}

      {showExceptionForm && (
        <div className="border rounded-lg p-3 space-y-3 bg-muted/10">
          <div className="space-y-2">
            <Label>{t("exceptions.type")}</Label>
            <Select
              value={exceptionType}
              onValueChange={(v: "holiday" | "custom") => setExceptionType(v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="holiday">
                  {t("exceptions.holiday")}
                </SelectItem>
                <SelectItem value="custom">{t("exceptions.custom")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {exceptionType === "holiday" && (
            <div className="space-y-2">
              <Label>{t("exceptions.selectHolidays")}</Label>
              <SearchableSelect
                items={availableHolidays.map((holiday) => ({
                  id: holiday.id,
                  label: holiday.name,
                  description: format(
                    getNextOccurrence({ rrule: holiday.rrule }) + "T00:00:00",
                    "EEE d MMM",
                  ),
                  data: holiday,
                }))}
                value={selectedHolidayIds}
                onChange={(v) =>
                  setSelectedHolidayIds(Array.isArray(v) ? v : [v])
                }
                multiple={true}
                placeholder={t("exceptions.holidaysPlaceholder")}
                searchPlaceholder={t("exceptions.holidaysSearch")}
                emptyText={t("exceptions.holidaysEmpty")}
                showSelectAll={true}
                showBadges={true}
              />
            </div>
          )}

          {exceptionType === "custom" && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label>{t("exceptions.date")}</Label>
                <Input
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("exceptions.name")}</Label>
                <Input
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder={t("exceptions.namePlaceholder")}
                />
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowExceptionForm(false);
                setSelectedHolidayIds([]);
                setCustomDate("");
                setCustomName("");
              }}
            >
              {t("exceptions.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={addException}
              disabled={
                (exceptionType === "holiday" &&
                  selectedHolidayIds.length === 0) ||
                (exceptionType === "custom" && (!customDate || !customName))
              }
            >
              {t("exceptions.confirm", {
                count:
                  exceptionType === "holiday" ? selectedHolidayIds.length : 1,
              })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
